/**
 * NodeDetailsPanel - Panel de detalles de nodo en el RightPanel
 *
 * @version 1.0
 * @created 2026-03-03
 * @module core/layouts/panels/RightPanel
 *
 * @description
 * Muestra detalles de un nodo de la red canónica seleccionado.
 * Permite ver y editar propiedades del nodo.
 *
 * @remarks
 * - Se abre cuando el usuario hace click en un nodo en el mapa o en la lista
 * - Muestra información geográfica, topológica y de origen
 * - Permite editar propiedades básicas
 */

import { useState, useEffect } from 'react';
import { X, CircleDot, MapPin, Layers, Info } from 'lucide-react';
import { canonicalNetworkApi } from '../../../spatial/services/canonical-network-api.service';
import type { CanonicalNode } from '../../../spatial/types';
import './node-details-panel.css';

/**
 * Props del componente NodeDetailsPanel
 */
export interface NodeDetailsPanelProps {
  /**
   * ID del nodo a mostrar
   */
  nodeId: string;

  /**
   * Callback cuando el usuario cierra el panel
   */
  onClose: () => void;
}

/**
 * Panel de detalles de nodo en el RightPanel.
 *
 * @remarks
 * Proporciona información completa sobre el nodo seleccionado.
 */
export function NodeDetailsPanel({
  nodeId,
  onClose,
}: NodeDetailsPanelProps): JSX.Element {
  const [node, setNode] = useState<CanonicalNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carga los detalles del nodo
   */
  useEffect(() => {
    const loadNode = async () => {
      setLoading(true);
      setError(null);
      try {
        const nodeData = await canonicalNetworkApi.getNode(nodeId);
        setNode(nodeData);
      } catch (err) {
        console.error('[NodeDetailsPanel] Error loading node:', err);
        setError('Error al cargar detalles del nodo');
      } finally {
        setLoading(false);
      }
    };

    void loadNode();
  }, [nodeId]);

  return (
    <div className="node-details-panel">
      {/* Header del panel */}
      <header className="node-details-panel-header">
        <div className="node-details-panel-title-group">
          <CircleDot size={18} />
          <h2 className="node-details-panel-title">Nodo</h2>
        </div>
        <button
          className="node-details-panel-close-btn"
          onClick={onClose}
          title="Cerrar panel"
          aria-label="Cerrar panel de detalles"
        >
          <X size={16} />
        </button>
      </header>

      {/* Contenido */}
      <div className="node-details-panel-content">
        {loading && (
          <div className="node-details-panel-loading">
            <div className="spinner" />
            <p>Cargando detalles...</p>
          </div>
        )}

        {error && (
          <div className="node-details-panel-error">
            <p>{error}</p>
          </div>
        )}

        {node && !loading && !error && (
          <>
            {/* ID */}
            <section className="node-details-section">
              <h3 className="node-details-section-title">
                <Info size={14} />
                Identificación
              </h3>
              <div className="node-details-field">
                <span className="node-details-label">ID:</span>
                <span className="node-details-value node-details-value-mono">{node.id}</span>
              </div>
              <div className="node-details-field">
                <span className="node-details-label">Versión:</span>
                <span className="node-details-value">{node.nodeVersion}</span>
              </div>
              <div className="node-details-field">
                <span className="node-details-label">Estado:</span>
                <span className={`node-details-badge node-details-badge-${node.status}`}>
                  {node.status === 'active' ? 'Activo' : 'Deprecado'}
                </span>
              </div>
            </section>

            {/* Ubicación */}
            <section className="node-details-section">
              <h3 className="node-details-section-title">
                <MapPin size={14} />
                Ubicación
              </h3>
              <div className="node-details-field">
                <span className="node-details-label">Longitud:</span>
                <span className="node-details-value node-details-value-mono">
                  {node.geometry.coordinates[0].toFixed(6)}°
                </span>
              </div>
              <div className="node-details-field">
                <span className="node-details-label">Latitud:</span>
                <span className="node-details-value node-details-value-mono">
                  {node.geometry.coordinates[1].toFixed(6)}°
                </span>
              </div>
            </section>

            {/* Topología */}
            <section className="node-details-section">
              <h3 className="node-details-section-title">
                <Layers size={14} />
                Topología
              </h3>
              <div className="node-details-field">
                <span className="node-details-label">Capa:</span>
                <span className="node-details-value">
                  {node.layer === 0 ? 'Nivel de suelo' : node.layer > 0 ? `Puente (+${node.layer})` : `Túnel (${node.layer})`}
                </span>
              </div>
              {node.intersectionType && (
                <div className="node-details-field">
                  <span className="node-details-label">Tipo de intersección:</span>
                  <span className="node-details-value">
                    {node.intersectionType === 'simple' ? 'Simple' :
                     node.intersectionType === 'roundabout' ? 'Rotonda' :
                     'Desnivel separado'}
                  </span>
                </div>
              )}
            </section>

            {/* Origen */}
            <section className="node-details-section">
              <h3 className="node-details-section-title">Origen</h3>
              <div className="node-details-field">
                <span className="node-details-label">Fuente:</span>
                <span className={`node-details-badge node-details-badge-${node.sourceType}`}>
                  {node.sourceType === 'osm' ? 'OpenStreetMap' :
                   node.sourceType === 'manual' ? 'Manual' :
                   node.sourceType === 'municipal' ? 'Municipal' :
                   'Importado'}
                </span>
              </div>
              {node.sourceReference && (
                <div className="node-details-field">
                  <span className="node-details-label">Referencia:</span>
                  <span className="node-details-value node-details-value-mono">{node.sourceReference}</span>
                </div>
              )}
              <div className="node-details-field">
                <span className="node-details-label">Autoridad manual:</span>
                <span className="node-details-value">
                  {node.isManualAuthority ? 'Sí' : 'No'}
                </span>
              </div>
            </section>

            {/* Metadata */}
            <section className="node-details-section">
              <h3 className="node-details-section-title">Metadata</h3>
              <div className="node-details-field">
                <span className="node-details-label">Creado:</span>
                <span className="node-details-value">
                  {new Date(node.createdAt).toLocaleString('es-AR')}
                </span>
              </div>
              <div className="node-details-field">
                <span className="node-details-label">Modificado:</span>
                <span className="node-details-value">
                  {new Date(node.updatedAt).toLocaleString('es-AR')}
                </span>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
